import dayjs from "dayjs";
import { message, Modal } from "antd";
import { TimeCalculationService, RotationService } from "./index";
import enhancedApi from "../../../../api/enhancedApi";
import { ensureFlightTimeConsistency, formatDuration, pickEditorDepartureTime, pickEditorArrivalTime } from "../../../../utils/timeUtils";
import { applyRouteBlockTimeStaPolicy, getCurrentBlockTimeMinutes } from "../../../../utils/staBlockTimeAlignment";
import { getAirportMinGroundTime } from "../../../../utils/minGroundTimeUtils";
import { pickUsableDefaultRoute } from "../../shared/routeBinding";
import ConstraintsService from "./ConstraintsService";

/**
 * 表格数据服务 - 处理表格数据操作相关逻辑
 */
class TableService {
    /**
     * 生成唯一ID
     * @param {string} prefix - ID前缀，用于标识来源，如'imported', 'copied'等
     * @param {string} originalId - 原始ID，可选
     * @returns {string} 生成的唯一ID
     */
    static generateUniqueId(prefix = "flight", originalId = "") {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 10000);
        // 添加一个静态计数器，确保同一毫秒内生成的ID也不会重复
        this._idCounter = (this._idCounter || 0) + 1;
        return `${prefix}-${timestamp}-${random}-${this._idCounter}${originalId ? `-${originalId}` : ""}`;
    }

    /**
     * 导入航班按"编辑器口径"（ETD 优先、回退 STD）整体平移日期基准。
     * 这样监控里改过 ETD 的航班，导入时也会按新 ETD 算起始日期，不再被 STD 拖回旧值。
     */
    static getImportBaseDate(flight) {
        const baseTimes = [pickEditorDepartureTime(flight), flight?.flight_date];

        for (const baseTime of baseTimes) {
            if (!baseTime) continue;

            const parsed = dayjs.utc(baseTime);
            if (parsed.isValid()) return parsed;
        }

        return null;
    }

    /**
     * 计算导入起始日期相对源航班最早计划日期的整体偏移天数。
     */
    static calculateImportDateOffsetDays(flightsToImport, importStartDate) {
        if (!importStartDate || !flightsToImport || flightsToImport.length === 0) return null;

        const targetStartDate = dayjs.utc(importStartDate);
        if (!targetStartDate.isValid()) return null;

        const flightDates = flightsToImport.map((flight) => this.getImportBaseDate(flight)).filter(Boolean);
        if (flightDates.length === 0) return null;

        const earliestDate = flightDates.reduce((earliest, current) => (current.isBefore(earliest) ? current : earliest));
        return targetStartDate.startOf("day").diff(earliestDate.startOf("day"), "day");
    }

    /**
     * 预览单个航班导入后的起飞时间，供弹窗预览和实际导入共用。
     * 取"编辑器口径"——ETD 优先、回退 STD，让监控里改过 ETD 的航班能正确反映新时间。
     */
    static getImportedStdPreview(flight, daysDiff) {
        if (daysDiff === null || daysDiff === undefined) return null;
        const baseDeparture = pickEditorDepartureTime(flight);
        if (!baseDeparture) return null;

        const originalStdDate = dayjs.utc(baseDeparture);
        if (!originalStdDate.isValid()) return null;

        return originalStdDate.add(daysDiff, "day").format("YYYY-MM-DDTHH:mm:ss[Z]");
    }

    /**
     * 过滤和排序航班
     * @param {Array} flights - 航班列表
     * @param {string} searchText - 搜索文本
     * @returns {Array} 过滤和排序后的航班
     */
    static filterAndSortFlights(flights, searchText) {
        if (!flights) return [];

        let result = flights;

        // 应用搜索过滤
        if (searchText) {
            const lowerSearchText = searchText.toLowerCase();
            result = flights.filter(
                (flight) =>
                    (flight.flight_number && flight.flight_number.toLowerCase().includes(lowerSearchText)) ||
                    (flight.departure_airport && flight.departure_airport.toLowerCase().includes(lowerSearchText)) ||
                    (flight.arrival_airport && flight.arrival_airport.toLowerCase().includes(lowerSearchText)) ||
                    (flight.flight_date && flight.flight_date.includes(lowerSearchText)),
            );
        }

        // 根据display_order排序
        return [...result].sort((a, b) => {
            // 优先按display_order排序
            if (a.display_order !== undefined && b.display_order !== undefined) {
                // 确保使用数字类型比较
                const aOrder = parseInt(a.display_order) || 0;
                const bOrder = parseInt(b.display_order) || 0;
                return aOrder - bOrder;
            }
            // 如果没有display_order, 则按照flight_date和flight_number排序
            if (a.flight_date !== b.flight_date) {
                return new Date(a.flight_date) - new Date(b.flight_date);
            }
            return a.flight_number.localeCompare(b.flight_number);
        });
    }

    /**
     * 添加航段
     * @param {Array} flights - 当前航班列表
     * @param {Array} legs - 要添加的航段
     * @param {number|null} selectedRowIndex - 选中行索引，插入位置
     * @param {string} scheduleId - 计划ID
     * @returns {Array} 更新后的航班列表
     */
    static addLegs(flights, legs, selectedRowIndex, scheduleId) {
        if (!legs || legs.length === 0) return flights;

        // 确保每个航班都有schedule_id
        const legsWithScheduleId = legs.map((leg) => ({
            ...leg,
            schedule_id: scheduleId,
        }));

        const newFlights = [...flights];

        // 处理在开头添加航段的情况
        if (selectedRowIndex === -1) {
            // 设置新插入航班的display_order为现有最小值减1，确保它们在最前面
            const minDisplayOrder = this.getDisplayOrder(flights, "min");
            const newLegs = legsWithScheduleId.map((leg, idx) => ({
                ...leg,
                display_order: minDisplayOrder - legsWithScheduleId.length + idx,
            }));
            newFlights.unshift(...newLegs);
        } else if (selectedRowIndex !== null) {
            // 获取插入位置的航班的display_order
            const currentDisplayOrder = flights[selectedRowIndex].display_order || selectedRowIndex + 1;
            // 将新航班的display_order设置为当前位置航班的值+0.1, +0.2等，确保它们被正确排序
            const newLegs = legsWithScheduleId.map((leg, idx) => ({
                ...leg,
                display_order: currentDisplayOrder + (idx + 1) * 0.1,
            }));
            newFlights.splice(selectedRowIndex + 1, 0, ...newLegs);
        } else {
            // 否则添加到末尾
            const maxDisplayOrder = this.getDisplayOrder(flights, "max");
            const newLegs = legsWithScheduleId.map((leg, idx) => ({
                ...leg,
                display_order: maxDisplayOrder + idx + 1,
            }));

            // 添加新航班
            return [...flights, ...newLegs];
        }

        // 重新标准化所有航班的display_order
        return newFlights.map((flight, idx) => ({
            ...flight,
            display_order: idx + 1,
        }));
    }

    /**
     * 复制航班
     * @param {Array} flights - 当前航班列表
     * @param {Array} selectedFlights - 选中的航班
     * @param {Object} copyOptions - 复制选项
     * @param {string} scheduleId - 计划ID
     * @returns {Array} 更新后的航班列表
     */
    static copyFlights(flights, selectedFlights, copyOptions, scheduleId) {
        const { copyStartDate, copyCount = 1 } = copyOptions;

        if (!copyStartDate || selectedFlights.length === 0) {
            message.warning(selectedFlights.length === 0 ? "没有选中任何航班" : "请选择起始日期");
            return flights;
        }

        try {
            // 获取选中航班中的最早日期
            const flightDates = selectedFlights.filter((f) => f.flight_date).map((f) => dayjs.utc(f.flight_date));

            if (flightDates.length === 0) {
                message.warning("选中的航班没有有效日期");
                return flights;
            }

            const earliestDate = flightDates.reduce((earliest, current) => (current.isBefore(earliest) ? current : earliest));

            // 确保起始日期是UTC格式，避免本地时区转换导致的偏差
            // 将copyStartDate解析为UTC日期，确保和earliestDate一致的时区解释
            const copyStartDateUTC = dayjs.utc(copyStartDate);

            // 计算日期偏移量（新起始日期 - 原始最早日期）
            // 使用startOf('day')确保两个日期都在日期的开始，避免时区差异
            const daysDiff = copyStartDateUTC.startOf("day").diff(earliestDate.startOf("day"), "day");

            let allCopiedFlights = [];

            // 按照复制次数循环创建副本
            for (let copyIndex = 0; copyIndex < copyCount; copyIndex++) {
                // 复制航班并调整日期
                const copiedFlights = selectedFlights.map((flight, flightIndex) => {
                    const newId = this.generateUniqueId(`copied${copyIndex}_${flightIndex}`, flight.id);

                    // 如果航班有日期，则根据偏移量调整
                    let newFlightDate = flight.flight_date;
                    let newStd = flight.std;
                    let newSta = flight.sta;

                    if (flight.std) {
                        // 计算目标日期 = 原始日期 + 日期偏移量 + 复制索引 * 7天
                        const originalStdDate = dayjs.utc(flight.std);

                        // 使用整数天数进行日期偏移，避免时区问题
                        const targetDate = originalStdDate.add(daysDiff + copyIndex * 7, "day");

                        // 处理起飞时间
                        const stdHourMinute = originalStdDate.format("HH:mm:ss");
                        const newStdDate = targetDate.format("YYYY-MM-DD");
                        newStd = `${newStdDate}T${stdHourMinute}Z`;

                        // 确保flight_date始终与std保持完全一致(包括时间部分)
                        newFlightDate = newStd;

                        // 处理到达时间
                        if (flight.sta) {
                            const staTime = dayjs.utc(flight.sta);
                            const staHourMinute = staTime.format("HH:mm:ss");

                            // 检查跨天情况
                            if (flight.std && flight.sta) {
                                const stdDay = dayjs.utc(flight.std).date();
                                const staDay = dayjs.utc(flight.sta).date();
                                const dayDiff = staDay - stdDay;

                                if (dayDiff > 0) {
                                    // 如果原始航班是跨天的，保持跨天关系
                                    const newStaDate = targetDate.add(dayDiff, "day").format("YYYY-MM-DD");
                                    newSta = `${newStaDate}T${staHourMinute}Z`;
                                } else {
                                    newSta = `${newStdDate}T${staHourMinute}Z`;
                                }
                            } else {
                                newSta = `${newStdDate}T${staHourMinute}Z`;
                            }
                        }
                    }

                    // 创建新航班对象
                    return {
                        ...flight,
                        id: newId,
                        originalId: flight.id,
                        copyIndex,
                        flight_date: newFlightDate,
                        std: newStd,
                        sta: newSta,
                        schedule_id: scheduleId,
                    };
                });

                // 将当前副本添加到所有航班列表
                allCopiedFlights = [...allCopiedFlights, ...copiedFlights];
            }

            // 获取当前航班列表中最大的display_order值
            const maxOrder = this.getDisplayOrder(flights, "max");

            // 为新复制的航班分配递增的display_order，保持被复制航班的原始顺序
            const copiedFlightsWithOrder = allCopiedFlights.map((flight, idx) => ({
                ...flight,
                display_order: maxOrder + idx + 1,
            }));

            message.success(`成功复制 ${selectedFlights.length} 个航班 ${copyCount} 次，共 ${copiedFlightsWithOrder.length} 个航班`);

            // 合并原有航班和新复制的航班
            return [...flights, ...copiedFlightsWithOrder];
        } catch (error) {
            console.error("复制航班失败:", error);
            message.error("复制航班失败");
            return flights;
        }
    }

    /**
     * 从草稿或操作计划导入航班
     * @param {Array} flights - 当前航班列表
     * @param {Array} flightsToImport - 要导入的航班
     * @param {Object} importOptions - 导入配置选项
     * @param {Date|string} importOptions.importStartDate - 导入起始日期
     * @param {string} importOptions.scheduleId - 计划ID
     * @param {Object} importOptions.state - 全局状态，包含约束等信息
     * @param {Array} legData - 航段数据，用于更新轮档时间和过站时间，可选
     * @returns {Promise<Array>} 导入后的航班列表
     */
    static async importFlights(flights, flightsToImport, importOptions, legData = []) {
        const { importStartDate, scheduleId, state, importRemarks = false, importBatch = null, onProgress = null } = importOptions;

        if (!importStartDate) {
            message.warning("请先设置导入起始日期");
            return flights;
        }

        if (!flightsToImport || flightsToImport.length === 0) {
            message.warning("没有可导入的航班");
            return flights;
        }

        // 【航段航路BH优化】记录导入失败的航段信息
        const failedLegs = [];
        const successfulFlights = [];
        const invalidRouteFlights = []; // 记录因缺少航路而验证失败的航班
        const missingGroundTimeAirports = new Set(); // 到达站缺最小过站配置的机场
        let staRewriteCount = 0;
        // 沿用源航路的班要与「这架飞机能用的默认」逐班比对；同一起降只取一次航路主数据
        const planAircraftReg = state.schedule?.aircraft?.registration || importOptions.state?.schedule?.aircraft?.registration;
        const legRoutesCache = new Map();

        console.log(`[导入校验] 开始导入 ${flightsToImport.length} 个航班...`);
        console.log(`[导入校验] 当前飞机注册号: ${state.schedule?.aircraft?.registration || "未选择"}`);

        // 导入前按涉及机场读取主数据当前值：导入不必先点“刷新基础数据”，
        // 与加航段、重算同一口径；拉取失败退回页面已加载的约束
        let constraintsForImport = state.constraints?.airportConstraints || {};
        try {
            const codes = Array.from(new Set(flightsToImport.flatMap((f) => [f.departure_airport, f.arrival_airport]).filter(Boolean)));
            if (codes.length > 0) {
                const freshConstraints = await ConstraintsService.fetchAirportConstraints(codes);
                constraintsForImport = { ...constraintsForImport, ...freshConstraints };
                // 回写页面约束状态：导入后表格展示的最小过站与本次导入同源，
                // 不必等切走切回才刷新
                if (typeof importOptions.setAirportConstraints === "function") {
                    importOptions.setAirportConstraints((prev) => ({ ...prev, ...freshConstraints }));
                }
            }
        } catch (refreshError) {
            console.error("导入前刷新机场约束失败:", refreshError);
        }

        try {
            // 获取要导入航班中的最早日期，用于计算日期偏移量
            const daysDiff = this.calculateImportDateOffsetDays(flightsToImport, importStartDate);

            if (daysDiff === null) {
                message.warning("导入的航班没有有效计划日期");
                return flights;
            }

            // 【航段航路BH优化】改为for循环，以便在航路匹配失败时跳过该航班
            for (let index = 0; index < flightsToImport.length; index++) {
                const flight = flightsToImport[index];
                onProgress?.(index + 1, flightsToImport.length);
                // 为每一个航班生成新ID并设置正确的计划ID
                // 添加索引确保ID唯一性
                let newFlight = {
                    ...flight,
                    id: this.generateUniqueId(`imported${index}`),
                    schedule_id: scheduleId,
                };

                // 根据importRemarks选项决定是否保留备注字段
                if (!importRemarks) {
                    // 如果不导入备注，则清空remarks字段
                    newFlight.remarks = "";
                }

                // 起飞时间按"编辑器口径"：ETD 优先、回退 STD；ATD 不参与。
                // 到达时间同理：ETA 优先、回退 STA；ATA 不参与。
                // 起飞和到达独立判断，互不绑定。
                const sourceDeparture = pickEditorDepartureTime(flight);
                const sourceArrival = pickEditorArrivalTime(flight);

                if (sourceDeparture) {
                    // 计算新的航班日期和起飞时间
                    const importedStd = this.getImportedStdPreview(flight, daysDiff);
                    if (!importedStd) {
                        continue;
                    }
                    const targetDate = dayjs.utc(importedStd);
                    const newStdDate = targetDate.format("YYYY-MM-DD");
                    newFlight.std = importedStd;

                    // 确保flight_date始终与std保持完全一致(包括时间部分)
                    newFlight.flight_date = newFlight.std;

                    // 处理到达时间
                    if (sourceArrival) {
                        const arrivalTime = dayjs.utc(sourceArrival);
                        const arrivalHourMinute = arrivalTime.format("HH:mm:ss");

                        // 跨天关系按"源起飞 vs 源到达"实际差判断，
                        // 而不是绑定 STD-STA 的旧关系，避免 ETA 跨天但 STA 不跨天的场景错位。
                        const departureSource = dayjs.utc(sourceDeparture);
                        if (departureSource.isValid() && arrivalTime.isValid()) {
                            const dayDiff = arrivalTime.startOf("day").diff(departureSource.startOf("day"), "day");

                            if (dayDiff > 0) {
                                const newStaDate = targetDate.add(dayDiff, "day").format("YYYY-MM-DD");
                                newFlight.sta = `${newStaDate}T${arrivalHourMinute}Z`;
                            } else {
                                newFlight.sta = `${newStdDate}T${arrivalHourMinute}Z`;
                            }
                        } else {
                            newFlight.sta = `${newStdDate}T${arrivalHourMinute}Z`;
                        }
                    }
                }

                // 【航段航路BH优化】校验绑定航路，失败则跳过该航班
                const aircraftReg = planAircraftReg;
                let routeMatch = null;

                // 必须有起降站信息
                if (!flight.departure_airport || !flight.arrival_airport) {
                    failedLegs.push({
                        flight_number: flight.flight_number || `第${index + 1}个航班`,
                        leg: `${flight.departure_airport || "?"}-${flight.arrival_airport || "?"}`,
                        reason: "缺少起降站信息",
                    });
                    continue; // 跳过此航班
                }

                // 有绑定航路时，轮档必须从当前航路主数据刷新，不能沿用源航班快照
                if (flight.flight_route_id) {
                    try {
                        const response = await enhancedApi.flightRoute.getFlightRouteById(flight.flight_route_id);
                        const route = response?.data;
                        if (route && route.block_time_minutes) {
                            routeMatch = {
                                flight_route_id: route.id,
                                min_block_time_minutes: route.block_time_minutes,
                                route_name: route.route_name,
                            };
                        }
                    } catch (error) {
                        console.warn(`为航班 ${flight.flight_number} 查询源航路 ${flight.flight_route_id} 失败:`, error);
                    }

                    if (routeMatch) {
                        console.log(`[导入校验] 航班 ${flight.flight_number} 刷新绑定航路: ${routeMatch.route_name} (ID: ${routeMatch.flight_route_id})`);
                    }
                } else if (aircraftReg) {
                    try {
                        const response = await enhancedApi.flightRoute.getBestMatchRoute(flight.departure_airport, flight.arrival_airport, aircraftReg);
                        const route = response?.data;
                        if (route && route.block_time_minutes) {
                            routeMatch = {
                                flight_route_id: route.id,
                                min_block_time_minutes: route.block_time_minutes,
                                route_name: route.route_name,
                            };
                        }
                    } catch (error) {
                        console.warn(`为航班 ${flight.flight_number} 自动匹配航路失败:`, error);
                    }

                    if (routeMatch) {
                        console.log(`[导入校验] 航班 ${flight.flight_number} 自动匹配航路: ${routeMatch.route_name} (ID: ${routeMatch.flight_route_id})`);
                    }
                }

                // 将最终确定的航路信息写入航班数据
                if (routeMatch && routeMatch.min_block_time_minutes) {
                    newFlight.flight_route_id = routeMatch.flight_route_id;
                    newFlight.flight_route_name = routeMatch.route_name;
                    newFlight.flight_route_block_time_minutes = routeMatch.min_block_time_minutes;
                }

                // 【核心校验】如果没有匹配到有效航路，记录失败并跳过
                if (!routeMatch || !routeMatch.min_block_time_minutes) {
                    const failureReason = flight.flight_route_id ? "绑定航路不存在或无有效轮档" : aircraftReg ? "无可匹配航路" : "源航班未绑定航路，且未选择飞机无法匹配";
                    console.warn(`[导入校验] 航班 ${flight.flight_number} (${flight.departure_airport}-${flight.arrival_airport}) 验证失败: ${failureReason}`);

                    failedLegs.push({
                        flight_number: flight.flight_number || `第${index + 1}个航班`,
                        leg: `${flight.departure_airport}-${flight.arrival_airport}`,
                        reason: failureReason,
                    });

                    // 创建包含基本信息的航班对象（无航路信息）
                    let tempFlightNoRoute = {
                        ...newFlight,
                        block_time_minutes: null,
                        min_block_time_minutes: null,
                        // 航路信息为空
                        flight_route_id: null,
                        flight_route_name: null,
                        flight_route_block_time_minutes: null,
                    };

                    // 最小过站只认到达机场主数据的明确配置（含 0 分钟）；机场没配就留空，
                    // 不得写 60。源航班上带过来的旧数字不作为配置使用
                    const minGroundTimeForNoRoute = getAirportMinGroundTime(constraintsForImport, flight.arrival_airport);
                    tempFlightNoRoute.min_ground_time_minutes = minGroundTimeForNoRoute;
                    if (minGroundTimeForNoRoute === null) {
                        missingGroundTimeAirports.add(flight.arrival_airport);
                    }

                    // 如果是从FlightOperation导入，需要补充一些字段
                    if (importOptions.importType === "operation") {
                        if (!tempFlightNoRoute.display_order) {
                            tempFlightNoRoute.display_order = flights.length + 1;
                        }
                    }

                    // 确保时间一致性
                    tempFlightNoRoute = ensureFlightTimeConsistency(tempFlightNoRoute);

                    invalidRouteFlights.push(tempFlightNoRoute);
                    continue; // 跳过后续正常处理，进入下一个循环
                }

                // 调试日志：确认航路匹配成功
                console.log(`[导入校验] ✅ 航班 ${flight.flight_number} (${flight.departure_airport}-${flight.arrival_airport}) 匹配到航路: ${routeMatch.route_name} (BH: ${routeMatch.min_block_time_minutes}分钟)`);

                // 【航段航路BH优化】实际轮档与最小轮档都从匹配航路获取
                const routeBlockTime = parseInt(routeMatch.min_block_time_minutes, 10);
                newFlight.min_block_time_minutes = routeBlockTime;
                newFlight.block_time_minutes = routeBlockTime;

                const beforeSta = newFlight.sta;
                if (importOptions.importType !== "operation") {
                    newFlight = applyRouteBlockTimeStaPolicy(newFlight, routeBlockTime);
                }
                if (beforeSta && newFlight.sta && beforeSta !== newFlight.sta) {
                    staRewriteCount += 1;
                }

                // 最小过站只认到达机场主数据的明确配置（含 0 分钟）；机场没配就留空，
                // 不得写 60（导入的实际地面时间也不是最小过站，不能拿来当配置）
                const minGroundTimeForImport = getAirportMinGroundTime(constraintsForImport, flight.arrival_airport);
                newFlight.min_ground_time_minutes = minGroundTimeForImport;
                if (minGroundTimeForImport === null) {
                    missingGroundTimeAirports.add(flight.arrival_airport);
                }

                // 如果是从FlightOperation导入，需要补充一些字段
                if (importOptions.importType === "operation") {
                    // 添加display_order字段
                    if (!newFlight.display_order) {
                        newFlight.display_order = flights.length + 1;
                    }
                }

                // 【航段航路BH优化】将成功的航班添加到列表
                successfulFlights.push(newFlight);
            } // 结束 for 循环

            // 【航段航路BH优化】处理导入结果
            console.log(`[导入校验] 导入完成 - 成功: ${successfulFlights.length}, 失败: ${failedLegs.length}`);

            // 处理失败的航班（缺少航路）
            if (failedLegs.length > 0) {
                // 询问用户是否忽略错误并导入
                const shouldIgnore = await new Promise((resolve) => {
                    Modal.confirm({
                        title: "导入警告",
                        width: 600,
                        content: (
                            <div>
                                <p style={{ marginBottom: 16, color: "#faad14" }}>{successfulFlights.length === 0 ? "所有航班因缺少可用航路而无法通过验证。" : `有 ${failedLegs.length} 个航班因缺少可用航路而无法通过验证。`}</p>
                                <div style={{ maxHeight: 300, overflow: "auto", marginBottom: 16 }}>
                                    <h4>验证失败的航班：</h4>
                                    <ul style={{ paddingLeft: 20 }}>
                                        {failedLegs.map((leg, idx) => (
                                            <li key={idx} style={{ marginBottom: 8 }}>
                                                <strong>{leg.flight_number}</strong> ({leg.leg})
                                                <br />
                                                <span style={{ color: "#ff4d4f", fontSize: 12 }}>原因: {leg.reason}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <p>是否忽略这些错误并强制导入？（导入后请手动补充航路信息）</p>
                            </div>
                        ),
                        okText: "忽略并导入",
                        cancelText: "取消导入", // 或者 "仅导入成功航班"
                        okButtonProps: { danger: true },
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                    });
                });

                if (shouldIgnore) {
                    // 用户选择忽略错误，将无效航班加入成功列表
                    successfulFlights.push(...invalidRouteFlights);
                    message.info(`已忽略验证错误，强制导入 ${invalidRouteFlights.length} 个航班`);
                } else {
                    // 用户选择取消，如果也没有成功的航班，则直接返回
                    if (successfulFlights.length === 0) {
                        return flights;
                    }
                    // 如果有成功的航班，继续导入成功的航班（保持原有逻辑）
                    message.info("已跳过验证失败的航班");
                }
            }

            // 如果没有成功导入任何航班
            if (successfulFlights.length === 0) {
                return flights;
            }

            // 沿用源航路的班（含从执行航班导入）逐班与「这架飞机能用的默认」比对：
            // 有不一致的就弹信息清单并在表格航路列打「非默认」标记，措辞中立，不评判对错
            const { nonDefaultRouteFlights, uncheckedLegs } = await this.collectNonDefaultRouteFlights(successfulFlights, {
                aircraftReg: planAircraftReg,
                getActiveRoutesForLeg: (departureAirport, arrivalAirport) => {
                    const legKey = `${departureAirport}-${arrivalAirport}`;
                    if (!legRoutesCache.has(legKey)) {
                        legRoutesCache.set(
                            legKey,
                            enhancedApi.flightRoute
                                .getRoutesByLeg(departureAirport, arrivalAirport, true)
                                .then((response) => response?.data || [])
                                .catch((error) => {
                                    console.warn(`[导入校验] 读取 ${legKey} 的航路主数据失败，本次不作「非默认」判断:`, error);
                                    return null;
                                }),
                        );
                    }
                    return legRoutesCache.get(legKey);
                },
            });

            if (nonDefaultRouteFlights.length > 0) {
                this.showNonDefaultRouteNotice(nonDefaultRouteFlights);
                if (typeof importOptions.onNonDefaultRoutes === "function") {
                    importOptions.onNonDefaultRoutes(nonDefaultRouteFlights);
                }
            }
            if (uncheckedLegs.length > 0) {
                message.warning(`以下航段未能读取航路主数据，本次未判断是否与默认一致：${uncheckedLegs.join("、")}`, 6);
            }

            // 【导入按STD排序】为导入的航班添加批次标记，用于在表格中区分多次导入
            const importedFlightsWithMark = successfulFlights.map((flight) => ({
                ...flight,
                _importBatch: importBatch, // 批次号，前端专用，不会发送到后端
            }));

            // 合并现有航班和导入航班
            const mergedFlights = [...flights, ...importedFlightsWithMark];

            // 按 STD 全局排序（而非追加到末尾）
            const sortedByStd = [...mergedFlights].sort((a, b) => {
                const stdA = a.std ? dayjs.utc(a.std).valueOf() : Infinity;
                const stdB = b.std ? dayjs.utc(b.std).valueOf() : Infinity;
                return stdA - stdB;
            });

            // 重新编号 display_order
            const newFlights = sortedByStd.map((flight, idx) => ({
                ...flight,
                display_order: idx + 1,
            }));

            message.success(`成功导入 ${successfulFlights.length} 个航班`);
            if (missingGroundTimeAirports.size > 0) {
                message.warning(`以下到达站未配置最小过站时间，导入后已留空：${Array.from(missingGroundTimeAirports).join("、")}。请先到机场主数据维护最小过站时间`, 8);
            }
            if (staRewriteCount > 0 && importOptions.importType !== "operation") {
                message.info(`已按匹配轮档更新 ${staRewriteCount} 班到达（起飞没动）`, 5);
            }

            return newFlights;
        } catch (error) {
            console.error("导入航班失败:", error);
            message.error("导入航班失败");
            return flights;
        }
    }

    /**
     * 逐班比对「绑的航路」与「这架飞机能用的默认航路」。
     * 只报不一致的班；没有可用默认的班（含默认只适配别的飞机）不提示、不打标。
     * 某个航段的航路主数据读不到时归入 uncheckedLegs，由调用方明确告知，不静默跳过。
     */
    static async collectNonDefaultRouteFlights(flights, { aircraftReg, getActiveRoutesForLeg } = {}) {
        const nonDefaultRouteFlights = [];
        const uncheckedLegs = [];

        for (const flight of flights || []) {
            if (!flight?.flight_route_id || !flight.departure_airport || !flight.arrival_airport) {
                continue;
            }

            const legKey = `${flight.departure_airport}-${flight.arrival_airport}`;
            const activeRoutes = await getActiveRoutesForLeg?.(flight.departure_airport, flight.arrival_airport);
            if (activeRoutes === null || activeRoutes === undefined) {
                if (!uncheckedLegs.includes(legKey)) {
                    uncheckedLegs.push(legKey);
                }
                continue;
            }

            const defaultRoute = pickUsableDefaultRoute(activeRoutes, aircraftReg);
            if (!defaultRoute || String(defaultRoute.id) === String(flight.flight_route_id)) {
                continue;
            }

            nonDefaultRouteFlights.push({
                flight_id: flight.id,
                flight_number: flight.flight_number || "未命名航班",
                leg: legKey,
                route_id: flight.flight_route_id,
                route_name: flight.flight_route_name || null,
                block_time_minutes: getCurrentBlockTimeMinutes(flight),
                default_route_id: defaultRoute.id,
                default_route_name: defaultRoute.route_name || null,
                default_block_time_minutes: defaultRoute.block_time_minutes ?? null,
            });
        }

        return { nonDefaultRouteFlights, uncheckedLegs };
    }

    /**
     * 导入后的信息清单：只说与默认不一致，不评判对错；并给出回归默认的入口。
     */
    static showNonDefaultRouteNotice(nonDefaultRouteFlights) {
        const formatBlockTime = (minutes) => (minutes ? formatDuration(minutes, "h_m") : "无有效轮档");

        Modal.info({
            title: "以下航班绑的航路与当前默认不一致",
            width: 640,
            content: (
                <div>
                    <div style={{ maxHeight: 320, overflow: "auto", marginBottom: 12 }}>
                        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                            {nonDefaultRouteFlights.map((item, idx) => (
                                <li key={idx} style={{ marginBottom: 8 }}>
                                    <strong>{item.flight_number}</strong> ({item.leg})
                                    <br />
                                    <span style={{ fontSize: 12, color: "#595959" }}>
                                        绑的航路：{item.route_name || item.route_id}（{formatBlockTime(item.block_time_minutes)}）
                                    </span>
                                    <br />
                                    <span style={{ fontSize: 12, color: "#595959" }}>
                                        当前默认：{item.default_route_name || item.default_route_id}（{formatBlockTime(item.default_block_time_minutes)}）
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <p style={{ marginBottom: 0 }}>点「刷新基础数据」可把这些航班统一回归默认；想保留当前选择，刷新后重新选一次即可。</p>
                </div>
            ),
            okText: "知道了",
        });
    }

    /**
     * 获取真实索引
     * @param {string} id - 航班ID
     * @param {Array} flights - 航班列表
     * @returns {number} 真实索引
     */
    static getRealIndex(id, flights) {
        return flights.findIndex((f) => f.id === id);
    }

    /**
     * 获取航班列表中的最大或最小display_order值
     * @param {Array} flights - 航班列表
     * @param {string} type - 'max'或'min'，默认为'max'
     * @param {number} defaultValue - 如果没有有效值时的默认值，max默认为0，min默认为1
     * @returns {number} 最大或最小的display_order值
     */
    static getDisplayOrder(flights, type = "max", defaultValue = null) {
        if (!flights || flights.length === 0) {
            return type === "max" ? (defaultValue !== null ? defaultValue : 0) : defaultValue !== null ? defaultValue : 1;
        }

        // 筛选有效的display_order值（防止NaN或undefined）
        const validOrders = flights.map((f) => Number(f.display_order)).filter((order) => !isNaN(order) && order > 0);

        if (validOrders.length === 0) {
            return type === "max" ? (defaultValue !== null ? defaultValue : 0) : defaultValue !== null ? defaultValue : 1;
        }

        return type === "max" ? Math.max(...validOrders) : Math.min(...validOrders);
    }

    /**
     * 清空所有航班
     * @param {Array} flights - 当前航班列表
     * @returns {Array} 清空后的航班列表（空数组）
     */
    static clearFlights(flights) {
        if (!flights || flights.length === 0) {
            message.info("当前没有航班数据");
            return [];
        }

        message.success("已清空所有航班");
        return [];
    }
}

export default TableService;
